import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    HostListener,
    NgZone,
    Signal,
    signal,
} from '@angular/core'
import { CalendarHeaderComponent } from '../calendar-header/calendar-header.component'
import { CalendarDaysComponent } from '../calendar-days/calendar-days.component'
import {
    CalendarRange,
    CalendarViewService,
} from '@app/routes/dashboard/services/calendar-view.service'
import { UserService } from '@app/core/_services/user.service'
import { Subject, timer } from 'rxjs'
import {
    MeetingsBookingsMeetingItem,
    MeetingsBookingsPairedMeeting,
    BookSessionPreferences,
    ConfirmedSessionsResponse,
    Container,
    MetPartnerInfo,
    User,
} from '@app/core/_models'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import {
    combineLatestWith,
    distinctUntilChanged,
    filter,
    finalize,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import { AvailableTimesService } from '@app/core/_services/available-times.service'
import {
    BookSessionInfo,
    BookSessionsService,
    ConfirmedSessionsService,
    FeatureCheckService,
    MeetingsService,
    ONE_HOUR_IN_MSECS,
    SelectedSession,
    SelectedSessionService,
    TWO_MINS_IN_MSECS,
    UtilsService,
} from '@app/core/_services'
import { NewUserCancelationService } from '@app/core/_services/new-user-cancelation.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import {
    UserRelationshipActionTaken,
    UserRelationshipService,
} from '@app/core/_services/user-relationship.service'
import { AnalyticsService, BookingSource } from '@app/core/_services/analytics.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { LoginSignupUtilService } from '@app/core/_services/login-signup-util.service'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { environment } from '@env/environment'
import { CommonModule } from '@angular/common'
import { ButtonModule } from 'primeng/button'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import {
    HOST_RECRUITMENT_BANNER_DISMISSED_KEY,
    HOST_RECRUITMENT_MODAL_VIEWED_KEY,
    LocalStorageAccessService,
} from '@app/core/_services/local-storage-access.service'
import { ViewportService } from '@app/core/_services/viewport.service'
import { DarkModeService } from '@app/core/_services/dark-mode.service'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared-dialogs/email-verify/email-verify-dialog.component'
import {
    AMBASSADOR_PROGRAM_MIN_SESSIONS,
    GROUP_MEETINGS_HOST_PROMPT_MIN_SESSIONS,
} from '@app/core/_services/feature-check.service'
import { StripeMigrationResponse, StripeService } from '@app/core/_services/stripe.service'
import { buildTallyFeedbackLink } from '@app/core/_utils/tally-feedback-link.utils'
import { GroupMeetingsStoreService } from '@app/core/_services/group-meetings-store.service'

export interface DayData {
    startOfDayInMs?: number
    isToday?: boolean
    isPast?: boolean
    slideDirection?: 'left' | 'right'
}

// KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
const FEATURE_DARK_MODE_MODAL_EXPIRY = 1761969600000 //Nov 1, 2025 12:00:00 AM ET
const FEATURE_TWENTY_FOUR_HOUR_BANNER_DISMISSED = 'twentyFourHourBannerDismissed'
const DATE_RANGE_CHANGE_QUERY_DEBOUNCE_MS = 800
const GROUP_MEETINGS_BETA_LAUNCH_DATE = [2026, 7, 10, 9]
const GROUP_MEETINGS_BETA_LAUNCH_TIME_ZONE = 'Europe/Berlin'

@Component({
    selector: 'fm-calendar',
    imports: [CalendarHeaderComponent, CalendarDaysComponent, CommonModule, ButtonModule],
    templateUrl: './calendar.component.html',
    styleUrl: './calendar.component.scss',
})
export class CalendarComponent {
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentUser: User
    private numberOfDaysToShow: number
    private calendarStartDayMidnight: number
    public sessionDuration: number
    public days: Array<DayData> = new Array<DayData>({})
    public initialized: boolean = false

    // Limit the amount of data we request to populate the calendar
    // based on what timeslots are currently visible to the user
    private endTimeToRetrieve: number = 0
    private pollTimerStarted: boolean = false
    private dateRange$ = new Subject<{ startTime: number; endTime: number }>()

    // Prevent unmatched and session reuqests from going out when window is
    // blured TODO: Add full logic
    private timedOut: boolean = false
    private blurred: boolean = false

    // Query confirmed sessions if unmatched session is starting
    private unmatchedSessionStartingTimeout = null

    public isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
    showFeatureBanner: boolean = false

    public selectedSessions: SelectedSession[] = []
    public bookingInProgress: boolean = false

    // Temporary until we figure out ghosting issues with
    // PrimeNg scrollpanel
    // A signal ensures async scrollbar measurements schedule this view in zoneless mode.
    public scrollbarWidth = signal<number | undefined>(undefined)

    // used for debouncing calls to getAvailableTimes
    private timeAtLastDateRangeChangeEvent: number | null = null
    private queryUnmatchedSessionsDebounceTimeout: any = null
    private queryAvailableTimesInProgress: boolean = false
    private lastNewUserCancelationKey: string | number | null = null

    constructor(
        private userService: UserService,
        private timeUtilitiesService: TimeUtilitiesService,
        private calendarViewService: CalendarViewService,
        private availableTimesService: AvailableTimesService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private meetingsService: MeetingsService,
        private sessionSettingsService: SessionSettingsService,
        private userRelationshipService: UserRelationshipService,
        private featureCheckService: FeatureCheckService,
        private analyticsService: AnalyticsService,
        private dialogService: DialogService,
        private lUtil: LoginSignupUtilService,
        private utilsService: UtilsService,
        private welcomeChecklistService: WelcomeChecklistService,
        private bookSessionsService: BookSessionsService,
        private errorHandlerService: ErrorHandlerService,
        private rewardsReferralService: RewardsReferralService,
        private ngZone: NgZone,
        public selectedSessionService: SelectedSessionService,
        private localStorageAccessService: LocalStorageAccessService,
        public viewportService: ViewportService,
        private darkModeService: DarkModeService,
        private cdr: ChangeDetectorRef,
        private newUserCancelationService: NewUserCancelationService,
        private stripeService: StripeService,
        public groupMeetingsStoreService: GroupMeetingsStoreService,
    ) {}

    @HostListener('window:focus', ['$event'])
    onFocus(event: any): void {
        this.blurred = false

        if (this.timedOut === true) {
            this.timedOut = false
            this.queryUnmatchedSessions(false)
        }
    }

    @HostListener('window:blur', ['$event'])
    onBlur(event: any): void {
        this.blurred = true
    }

    @HostListener('window:mousemove', ['$event'])
    onMouseMove(event: any): void {
        if (this.blurred === true && this.timedOut === true) {
            this.timedOut = false
            this.queryUnmatchedSessions(true)
        }
    }

    ngOnInit() {
        this.waitForRequiredDataToInitialize()
        this.registerForUserUpdates()
        this.registerForBlockStatusUpdates()
        this.registerForConfirmedSessionUpdates()
        this.registerForSelectedSessionUpdates()
        this.registerForDateRangeUpdates()
        this.registerForSessionDurationUpdates()

        this.availableTimesService.daysBeingLoaded$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((daysBeingLoaded) => {
                this.queryAvailableTimesInProgress = daysBeingLoaded.size > 0
            })
    }

    public onReceiveScrollbarWidth(width: number): void {
        setTimeout(() => {
            this.scrollbarWidth.set(width)
        })
    }

    private waitForRequiredDataToInitialize(): void {
        this.userService.currentUser
            .pipe(
                filter((currentUser) => currentUser?.timeZone != null),
                switchMap((initialUser) => {
                    // Needed to correctly initialize the calendar header and receive
                    // the calendarStartTime
                    this.currentUser = initialUser

                    return this.userService.currentUser.pipe(
                        combineLatestWith(
                            this.calendarViewService.calendarRange$,
                            this.sessionSettingsService.sessionDurationSetting$,
                        ),
                        filter(
                            ([currentUser, calendarRange, sessionDuration]) =>
                                currentUser?.timeZone != null &&
                                calendarRange !== null &&
                                calendarRange.numberOfDaysToShow !== undefined,
                        ),
                    )
                }),
                take(1),
            )
            .subscribe(([currentUser, calendarRange, sessionDuration]) => {
                this.currentUser = currentUser
                this.sessionDuration = sessionDuration
                this.setDateRange(calendarRange)

                // make sure we have all the above information before we mark the calendar as initialized
                this.initialized = true

                // Needed to avoid warning in dev enviroment
                this.cdr.detectChanges()

                this.startSessionDataQueries()

                this.triggerNotifications(this.currentUser)
            })
    }

    private displayNewLookDialog() {
        if (this.featureCheckService.isNewLayoutEnabled(this.currentUser)) {
            // for new users that start on the redesigned layout, don't new look dialog
            if (this.currentUser.features.newLayoutOnly) {
                this.localStorageAccessService.setItem('viewedDialog:newLookBetaWelcome', 'true')
                return
            }

            // User's on the beta should see the initial welcome dialog when they land
            // on the new dashboard. This should only happen once per device/browser
            if (
                this.localStorageAccessService.getItem('viewedDialog:newLookBetaWelcome') !== 'true'
            ) {
                this.localStorageAccessService.setItem('viewedDialog:newLookBetaWelcome', 'true')
                this.dialogService.openNewLookBetaWelcomeDialog()
            }
        }
    }

    private displayNewInterfaceReleasedDialog(user: User): boolean {
        if (
            user.features.newLayoutOnly &&
            this.localStorageAccessService.getItem('viewedDialog:interfaceUpdated') !== 'true'
        ) {
            this.localStorageAccessService.setItem('viewedDialog:interfaceUpdated', 'true')
            this.dialogService.openNewInterfaceBeatCompleteDialog(false)
            return true
        }

        // If not newLayoutOnly and created before all newLayoutOnly was assigned to all new users,
        // then they must be a user that had the old interface available
        if (
            user.createdOn < 1760473110000 &&
            this.localStorageAccessService.getItem('viewedDialog:newLookBetaComplete') !== 'true'
        ) {
            this.localStorageAccessService.setItem('viewedDialog:newLookBetaComplete', 'true')
            this.dialogService.openNewInterfaceBeatCompleteDialog(true)
            return true
        }
        return false
    }

    private registerForDateRangeUpdates() {
        this.calendarViewService.calendarRange$
            .pipe(
                filter((range) => range !== null && range.numberOfDaysToShow !== undefined),
                distinctUntilChanged(
                    (prev, curr) =>
                        prev?.startTime === curr?.startTime &&
                        prev?.numberOfDaysToShow === curr?.numberOfDaysToShow,
                ),
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe((range) => this.setDateRange(range))
    }

    private setDateRange(range: CalendarRange) {
        if (
            this.calendarStartDayMidnight === range.startTime &&
            this.numberOfDaysToShow === range.numberOfDaysToShow
        ) {
            return
        }

        this.calendarStartDayMidnight = range.startTime
        this.numberOfDaysToShow = range.numberOfDaysToShow
        this.endTimeToRetrieve = this.calculateEndTimeToRetrieve()
        this.updateDayArrays()

        // if we were previously waiting on a debounce for a previous date range, clear that debounce.
        if (this.queryUnmatchedSessionsDebounceTimeout) {
            clearTimeout(this.queryUnmatchedSessionsDebounceTimeout)
            this.queryUnmatchedSessionsDebounceTimeout = null
        }

        const now = this.utilsService.getCurrentTimestamp()
        const previousTimeAtLastDateRangeChangeEvent = this.timeAtLastDateRangeChangeEvent
        this.timeAtLastDateRangeChangeEvent = now

        if (
            (previousTimeAtLastDateRangeChangeEvent &&
                now - previousTimeAtLastDateRangeChangeEvent < 1000) ||
            this.queryAvailableTimesInProgress
        ) {
            // it's been less than the minimum allowed time since the last date range change event,
            // OR there is a query in progress. user is probably scrolling through the calendar quickly.
            // if they park on this page for a while, run the query.
            const timeToWait = this.queryAvailableTimesInProgress
                ? DATE_RANGE_CHANGE_QUERY_DEBOUNCE_MS
                : DATE_RANGE_CHANGE_QUERY_DEBOUNCE_MS -
                  (now - previousTimeAtLastDateRangeChangeEvent)
            this.queryUnmatchedSessionsDebounceTimeout = setTimeout(() => {
                // note that the previous query could still be running. assume this isn't a problem,
                // but if it is we can tackle it later.
                this.queryUnmatchedSessions(true)
            }, timeToWait)
        } else {
            // debounce time has passed, and there is no query in progress.
            this.queryUnmatchedSessions(true)
        }
    }

    private registerForUserUpdates(): void {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.currentUser = user
        })
    }

    private registerForSessionDurationUpdates(): void {
        this.sessionSettingsService.sessionDurationSetting$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((sessionDuration) => {
                this.sessionDuration = sessionDuration
            })
    }

    private registerForBlockStatusUpdates(): void {
        this.userRelationshipService.userRelationUpdated
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((actionData: UserRelationshipActionTaken) => {
                if (
                    actionData.action === 'block' ||
                    actionData.action === 'unblock' ||
                    actionData.action === 'bulk-unblock' ||
                    actionData.action === 'snooze' ||
                    actionData.action === 'blocks-to-snoozes' ||
                    actionData.action === 'unsnooze'
                ) {
                    // Refresh our available times data to reflect
                    // the new status
                    this.queryUnmatchedSessions(true, true)
                }
            })
    }

    private registerForSelectedSessionUpdates(): void {
        this.selectedSessionService.selectedSessions$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedSessions: SelectedSession[]) => {
                this.selectedSessions = [...selectedSessions]
            })
    }

    private registerForConfirmedSessionUpdates(): void {
        this.meetingsService.meetingsBookingsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((eventsBookingsState) => {
                this.checkForTodayChanges()
                this.querySessionIfUnmatchedAtStartTimeFromBookingEvents(
                    eventsBookingsState.meetings,
                )
            })

        this.meetingsService.lateSessionCanceled
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((wasCanceled) => {
                if (wasCanceled === true) {
                    this.dialogService.openLateSessionCanceledNotification()
                }
            })

        this.meetingsService.forceRematchNeeded
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((session) => {
                if (session) {
                    this.dialogService.openForceRematchModal(
                        session.id,
                        session.startMs,
                        session.durationMs,
                    )
                }
            })

        this.newUserCancelationService.newUserCancelation$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((cancelation) => this.handleNewUserCancelation(cancelation))
    }

    private startSessionDataQueries() {
        // Start Polling Confirmed/Unmatched Sessions
        if (this.endTimeToRetrieve && !this.pollTimerStarted) {
            this.pollTimerStarted = true
            let temp_sub: any = timer(0, 120000)

            this.ngZone.runOutsideAngular(() => {
                temp_sub.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
                    this.ngZone.run(() => {
                        this.queryUnmatchedSessions(false)
                        if (this.blurred) {
                            this.timedOut = true
                        }
                    })
                })
            })
        }
    }

    private queryUnmatchedSessions(override: boolean, bypassCache: boolean = false) {
        this.checkForTodayChanges()
        if (this.blurred === false || override) {
            this.availableTimesService
                .queryAvailableTimes(
                    this.calendarStartDayMidnight,
                    this.endTimeToRetrieve,
                    bypassCache,
                )
                .subscribe()
            if (this.featureCheckService.groupMeetingsEnabledSig()) {
                this.groupMeetingsStoreService.loadAvailableGroupMeetings(
                    bypassCache,
                    this.calendarStartDayMidnight,
                    this.endTimeToRetrieve,
                )
            }
        }
    }

    private querySessionIfUnmatchedAtStartTime(sessions: Container<ConfirmedSessionsResponse>) {
        // if there is an unmatched session starting in the next 2 minutes,
        // schedule a query to update its info when the session starts
        const length = sessions.length()
        let nowEpoch = this.utilsService.getCurrentTimestamp()
        this.clearUnmatchedSessionStartingTimeout()

        for (let i = 0; i < length; ++i) {
            const session = sessions.getByIndex(i)
            const timeDifferenceInMilliSeconds = session.sessionTime - nowEpoch

            if (
                timeDifferenceInMilliSeconds >= 0 &&
                timeDifferenceInMilliSeconds < TWO_MINS_IN_MSECS &&
                !session.partnerId
            ) {
                this.unmatchedSessionStartingTimeout = setTimeout(() => {
                    this.confirmedSessionsService.updateConfirmedSession(session.externalId)
                    this.clearUnmatchedSessionStartingTimeout()
                }, timeDifferenceInMilliSeconds)
            }
        }
    }

    private querySessionIfUnmatchedAtStartTimeFromBookingEvents(
        bookingMeetings: MeetingsBookingsMeetingItem[],
    ) {
        const nowEpoch = this.utilsService.getCurrentTimestamp()
        this.clearUnmatchedSessionStartingTimeout()

        for (let i = 0; i < bookingMeetings.length; ++i) {
            const meeting = bookingMeetings[i]
            if (!this.isPairedMeeting(meeting)) {
                continue
            }

            const timeDifferenceInMilliSeconds = meeting.startMs - nowEpoch
            if (
                timeDifferenceInMilliSeconds >= 0 &&
                timeDifferenceInMilliSeconds < TWO_MINS_IN_MSECS &&
                !meeting.partnerId
            ) {
                this.unmatchedSessionStartingTimeout = setTimeout(() => {
                    this.confirmedSessionsService
                        .getFullSessionInfoByExternalIds([meeting.id])
                        .subscribe((sessions) => {
                            const session = sessions[meeting.id] ?? null
                            this.meetingsService.updatePairedMeetingFromConfirmedSession(
                                meeting.id,
                                session,
                            )
                        })
                    this.clearUnmatchedSessionStartingTimeout()
                }, timeDifferenceInMilliSeconds)
            }
        }
    }

    private isPairedMeeting(
        meeting: MeetingsBookingsMeetingItem,
    ): meeting is MeetingsBookingsPairedMeeting {
        return meeting.meetingType === 'paired'
    }

    private clearUnmatchedSessionStartingTimeout() {
        if (this.unmatchedSessionStartingTimeout) {
            clearTimeout(this.unmatchedSessionStartingTimeout)
            this.unmatchedSessionStartingTimeout = null
        }
    }

    private handleNewUserCancelation(cancelation: MetPartnerInfo | null): void {
        if (!cancelation) {
            this.lastNewUserCancelationKey = null
            return
        }

        const cancelationKey = cancelation.sessionTime ?? cancelation.userId
        if (this.lastNewUserCancelationKey === cancelationKey) {
            return
        }

        this.lastNewUserCancelationKey = cancelationKey
        this.openNewUserCancelationDialog(cancelation)
    }

    private openNewUserCancelationDialog(cancelation: MetPartnerInfo): void {
        this.dialogService.openReportNewUserPromptDialog(
            cancelation.displayName,
            cancelation.firstName,
            cancelation.photoUrl || '',
            cancelation.sessionCount,
            cancelation.sessionTime,
            cancelation.accountDeleted,
            cancelation.profileUrl,
            (result) => {
                if (result === 'report') {
                    this.dialogService.openReportProfile(
                        cancelation.userId,
                        cancelation.photoUrl || '',
                        cancelation.firstName,
                        cancelation.displayName,
                        'reportNewUserPrompt',
                    )
                }
            },
        )
    }

    private checkForTodayChanges(): void {
        // Update calendar if the day marked as today changes
        let updateDays = false

        for (let i = 0; i < this.days.length; i++) {
            if (
                this.days[i].isToday !==
                this.timeUtilitiesService.isToday(
                    this.days[i].startOfDayInMs,
                    this.currentUser.timeZone,
                )
            ) {
                updateDays = true
                break
            }
        }

        if (updateDays) {
            this.updateDayArrays()
        }
    }

    private updateDayArrays(): void {
        if (this.calendarStartDayMidnight && this.numberOfDaysToShow) {
            // important! keep these days sorted by startOfDayInMs
            this.days = Array.from({ length: this.numberOfDaysToShow }, (_, index) => {
                const startOfDayInMs = moment(this.calendarStartDayMidnight)
                    .add(index, 'day')
                    .valueOf()
                const endOfDayInMs = this.timeUtilitiesService.getEndOfDay(
                    startOfDayInMs,
                    this.currentUser.timeZone,
                )
                const isPast = endOfDayInMs < this.utilsService.getCurrentTimestamp()
                const isToday = this.timeUtilitiesService.isToday(
                    startOfDayInMs,
                    this.currentUser.timeZone,
                )

                return { startOfDayInMs, isToday, isPast }
            })
        }
    }

    private calculateEndTimeToRetrieve(): number {
        if (this.numberOfDaysToShow && this.calendarStartDayMidnight) {
            return this.timeUtilitiesService.addDays(
                this.calendarStartDayMidnight,
                this.numberOfDaysToShow,
                this.currentUser.timeZone,
            )
        }
        return 0
    }

    triggerNotifications(user: User) {
        setTimeout(() => {
            if (this.lUtil.getComingFromSignup()) {
                this.lUtil.setComingFromSignUp(false)
                this.lUtil.markOnboardingComplete(user)
            }

            if (this.isWelcomeChecklistActive()) {
                this.welcomeChecklistService.openWelcomeChecklistAtPageLoad()
            }

            //if (!this.displayNewInterfaceReleasedDialog(user)) {
            // If not showing interface release modal
            // show dark mode releaes modal
            //this.displayFeatureModal(user)
            //}

            this.displayFocusNowUnlockedModal(user)
            this.displayGroupInviteNotifications(user)
            this.displayAmbassadorProgramModal(user)
            this.displayYearInReviewModal(user)
            this.displayPricingOptions(user)
            this.displayLabsAnnouncement(user)
            this.displayGroupMeetingHostRecruitment(user)
            this.displayGroupMeetingsHostPrompt(user)
            this.displayGroupMeetingsHostBetaAnnouncement(user)
            this.displayGroupMeetingsBetaLaunchModal(user)
            this.displayGroupSessionsUpgradesModal(user)
        }, 500)
    }

    displayGroupSessionsUpgradesModal(user: User): void {
        if (
            this.featureCheckService.isGroupMeetingsEnabled(user) &&
            this.featureCheckService.shouldAnnounceGroupSessionsUpgrades(user)
        ) {
            this.dialogService.openGroupSessionsUpgradesDialog(() => {
                this.featureCheckService.setGroupSessionsUpgradesAnnounced(user)
            })
        }
    }

    displayGroupMeetingsBetaLaunchModal(user: User): void {
        const launchDate = moment
            .tz(GROUP_MEETINGS_BETA_LAUNCH_DATE, GROUP_MEETINGS_BETA_LAUNCH_TIME_ZONE)
            .valueOf()
        if (
            Date.now() >= launchDate &&
            this.featureCheckService.isGroupMeetingsEnabled(user) &&
            !this.featureCheckService.hasViewedGroupMeetingsBetaLaunchModal(user)
        ) {
            this.dialogService.openGroupSessionsLaunchDialog(() => {
                this.featureCheckService.setHasViewedGroupMeetingsBetaLaunchModal(user)
            })
        }
    }

    displayNewUserFeedbackRequestModal(user: User) {
        // display feedback request for new users who have completed at least one session,
        // as long as the welcome checklist is out of the way
        if (
            user.features.newLayoutOnly &&
            !this.featureCheckService.hasViewedNewLayoutFeedbackRequest(user) &&
            user.compSessions >= 1 &&
            !this.isWelcomeChecklistActive()
        ) {
            this.dialogService.openNewLookFeedbackRequestDialog((res) => {
                this.featureCheckService.setHasViewedNewLayoutFeedbackRequest(user)
                if (res) {
                    window.open(
                        buildTallyFeedbackLink({
                            email: user.email,
                        }),
                        '_blank',
                    )
                }
            })
        }
    }

    displayFocusNowUnlockedModal(user: User) {
        if (
            this.featureCheckService.canUseNowSessions(user) &&
            !this.featureCheckService.hasViewedFocusNowUnlockedModal(user)
        ) {
            this.dialogService.openFocusNowUnlockedModal()
            this.featureCheckService.setHasViewedFocusNowUnlockedModal(user)
        }
    }

    displayDomainUpdatedNotificationModal(user: User) {
        // Only show the domain updated notification if the user has not yet seen it
        // and if we are not showing the "domain will update" notification (Ie. This is the
        // app.focusmate.com version of the application)
        if (
            !this.featureCheckService.hasViewedDomainUpdatedNotification(user) &&
            environment.showDomainUpdatedConfirmation
        ) {
            this.dialogService.openDomainUpdatedNotificationModal()
        }
    }

    // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
    displayFeatureModal(user: User) {
        const darkModeEnabled = this.localStorageAccessService.getItem('darkModeEnabled')
        const now = this.utilsService.getCurrentTimestamp()
        if (darkModeEnabled == null && now < FEATURE_DARK_MODE_MODAL_EXPIRY) {
            this.darkModeService.set(false)
            this.dialogService.openDarkModeReleaseModal()
        }
    }

    // KEEPING THIS CODE AROUND FOR NEXT YEAR IN FOCUS
    displayYearInReviewModal(user: User) {
        if (user.yearlyReview) {
            if (!this.featureCheckService.hasViewedYearInReviewModal(user)) {
                this.dialogService.openYearInReviewModal()
            }
        }
    }

    displayGroupInviteNotifications(user: User) {
        let groupInfo = user.group

        for (let group of groupInfo) {
            if (group.groupMember === false && group.groupInviteNotified === false) {
                this.dialogService.openGroupInviteNotification(group, this.currentUser.photo)
            }
        }
    }

    displayAmbassadorProgramModal(user: User) {
        if (
            user.compSessions >= AMBASSADOR_PROGRAM_MIN_SESSIONS &&
            !this.featureCheckService.hasViewedAmbassadorProgramModal(user) &&
            !this.isWelcomeChecklistActive()
        ) {
            this.dialogService.openAmbassadorPromptDialog(user.compSessions, (result) => {
                this.featureCheckService.setHasViewedAmbassadorProgramModal(user)
                if (result === true) {
                    // User clicked "Great, I'm in!" - open the settings modal
                    this.dialogService.openEditAmbassadorMatchFrequencySettingDialog(true)
                }
            })
        }
    }

    displayPricingOptions(user: User) {
        const timestamp = this.localStorageAccessService.getItem('priceOptionsReminder')
        const currentTimestamp = this.utilsService.getCurrentTimestamp()

        // 1. Guard Clause: Exit early if we aren't due for a reminder
        if (timestamp && +currentTimestamp < +timestamp) {
            return
        }

        if (!user.planInfo.subscribedLegacy) {
            return
        }

        this.stripeService
            .getPriceUpdateOptions()
            .pipe(
                filter((options) => !this.hasValidPricingOptionResponses(options)),
                switchMap(
                    (options) => this.dialogService.openPriceUpdateOptionsDialog(options).onClose,
                ),
                take(1),
            )
            .subscribe({
                next: () => {
                    const nextReminder =
                        this.utilsService.getCurrentTimestamp() + 20 * 60 * 60 * 1000 // 20 hours
                    this.localStorageAccessService.setItem(
                        'priceOptionsReminder',
                        nextReminder.toString(),
                    )
                },
                error: (err) => console.error('Failed to load pricing options', err),
            })
    }

    displayLabsAnnouncement(user: User) {
        const needsToSAcknowledgement =
            user.terms.accepted === false && user.terms.latestVersion === environment.tosVersion

        if (
            this.featureCheckService.isLabsAccessEnabled(user) &&
            (!this.featureCheckService.hasViewedLabsAnnouncementModal(user) ||
                needsToSAcknowledgement)
        ) {
            this.dialogService.openLabsAnnouncementModal(
                () => {
                    if (!needsToSAcknowledgement) {
                        this.featureCheckService.setHasViewedLabsAnnouncementModal(user)
                    }
                },
                needsToSAcknowledgement,
                user,
            )
        }
    }

    displayGroupMeetingsHostBetaAnnouncement(user: User): void {
        if (
            this.featureCheckService.groupMeetingsCreateEnabledSig() &&
            !this.featureCheckService.hasViewedGroupMeetingsHostBetaModal(user)
        ) {
            this.dialogService.openGroupMeetingsHostBetaAnnouncementDialog(() => {
                this.featureCheckService.setHasViewedGroupMeetingsHostBetaModal(user)
            })
        }
    }

    displayGroupMeetingHostRecruitment(user: User): void {
        if (
            !this.featureCheckService.isGroupMeetingsEnabled(user) &&
            !this.featureCheckService.isGroupMeetingsCreateEnabled(user) &&
            this.featureCheckService.isHostRecruitmentEnabled(user) &&
            this.localStorageAccessService.getItem(HOST_RECRUITMENT_MODAL_VIEWED_KEY) !== 'true' &&
            this.localStorageAccessService.getItem(HOST_RECRUITMENT_BANNER_DISMISSED_KEY) !== 'true'
        ) {
            this.dialogService.openGroupMeetingHostRecruitmentDialog(() => {
                this.localStorageAccessService.setItem(HOST_RECRUITMENT_MODAL_VIEWED_KEY, 'true')
            })
        }
    }

    displayGroupMeetingsHostPrompt(user: User): void {
        if (
            this.featureCheckService.isGroupMeetingsHostApplicationEnabled(user) &&
            (user.compGroupSessions ?? 0) >= GROUP_MEETINGS_HOST_PROMPT_MIN_SESSIONS &&
            !this.featureCheckService.hasViewedGroupMeetingsHostPromptModal(user)
        ) {
            this.dialogService.openGroupMeetingApplyToHostDialog()
        }
    }

    hasValidPricingOptionResponses(options: StripeMigrationResponse): boolean {
        if (options.annualUpgradeAvailable && options.convertToAnnual == null) {
            return false
        }
        if (options.localCurrencyAvailable && options.convertToLocalCurrency == null) {
            return false
        }

        return true
    }

    /* Banner and Feature modal logic */
    onClickBannerDismissed() {
        // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
        this.showFeatureBanner = false
        try {
            localStorage.setItem(FEATURE_TWENTY_FOUR_HOUR_BANNER_DISMISSED, 'true')
        } catch (e) {}
    }

    onClickClearAll() {
        this.selectedSessionService.deleteAllSelectedSessions()
    }

    onClickBookSessions() {
        const selectedSessionLength = this.selectedSessions.length
        let bookSessionInfo: BookSessionInfo[] = []

        for (let i = 0; i < selectedSessionLength; i++) {
            const selectedSession: SelectedSession = this.selectedSessions[i]
            const preferences: BookSessionPreferences = {
                favorites: {
                    value: selectedSession.sessionPreferenceFavorites,
                },
                quietMode: {
                    value: selectedSession.quietMode,
                },
            }

            bookSessionInfo.push({
                sessionTime: selectedSession.sessionTime,
                duration: selectedSession.sessionDuration,
                title: '',
                userAvailabilityCode: '',
                userId: '',
                preferences,
                activityType: selectedSession.activityType,
            })
        }

        this.bookingInProgress = true
        this.bookSessionsService
            .bookSessions(bookSessionInfo, this.currentUser.timeZone, BookingSource.CalendarFooter)
            .pipe(
                take(1),
                finalize(() => (this.bookingInProgress = false)),
            )
            .subscribe(
                (res) => {
                    let parsedRes = this.bookSessionsService.parseBookResponse(res)

                    if (parsedRes['numSuccess'] !== 0) {
                        this.dialogService.openConfirmTransaction(
                            parsedRes['numSuccess'] === 1
                                ? parsedRes['numSuccess'] + ' session booked!'
                                : parsedRes['numSuccess'] + ' sessions booked!',
                            3000,
                            { desktopOnly: true },
                        )
                    }

                    if (this.currentUser.emailVerified === false) {
                        this.dialogService.openEmailVerificationDialog(
                            MODAL_VERIFY_EMAIL_BOOKING,
                            () => {
                                this.processParsedResult(parsedRes)
                            },
                        )
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.errorHandlerService.handleErrorResponse(error)
                    this.selectedSessionService.deleteAllSelectedSessions()
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.currentUser.properties.viewedSegmentationSurveyModal &&
            this.currentUser.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.currentUser)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    ngOnDestroy() {
        this.clearUnmatchedSessionStartingTimeout()
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
